// Ruta: packages/auth/src/ProtectedRoute.tsx
// Route guard that redirects to /login if not authenticated

import React, { type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from './auth-store.js'

interface ProtectedRouteProps {
  children: ReactNode
  /** If provided, check that the user has at least one of these roles */
  roles?: string[]
  /** If provided, check that the user has all of these permissions */
  permissions?: string[]
  /** Custom redirect path if unauthorized (defaults to /unauthorized) */
  unauthorizedRedirect?: string
}

export function ProtectedRoute({
  children,
  roles,
  permissions,
  unauthorizedRedirect = '/unauthorized',
}: ProtectedRouteProps): React.ReactElement {
  const location = useLocation()
  const { isAuthenticated, isLoading, user, hasRole, hasPermission } = useAuthStore()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  if (!isAuthenticated) {
    const returnUrl = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />
  }

  if (roles && roles.length > 0 && user) {
    const hasRequiredRole = roles.some((role) => hasRole(role))
    if (!hasRequiredRole) {
      return <Navigate to={unauthorizedRedirect} replace />
    }
  }

  if (permissions && permissions.length > 0 && user) {
    const hasAllPermissions = permissions.every((p) => hasPermission(p))
    if (!hasAllPermissions) {
      return <Navigate to={unauthorizedRedirect} replace />
    }
  }

  return <>{children}</>
}
